import React, { Fragment, useCallback, useContext, useEffect, useReducer } from 'react';
import { isEmpty, values, kebabCase } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { constants } from '@clubmed/utils';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import type { AddOrRemoveInsuranceFromProposal } from 'Store/booking/proposals/proposals.action';
import {
  deleteInsurances,
  addInsurances,
  refuseInsuranceAction,
} from 'Store/booking/proposals/proposals.action';
import { renderInfoBanner } from 'Components/widgets/info-banner/infoBanner.utils';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import 'Components/widgets/info-banner/infoBanner.query';
import { PromoCode } from 'Components/widgets/promo-code/PromoCode';
import { DiscountsLine } from 'Components/widgets/promo-code/DiscountsLine';
import { ICONS } from 'Components/utils/icon/icons';
import { submitOption } from 'Components/widgets/contact-info/ContactInfoUtils';
import { Title } from 'Components/frameworks/title/Title';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { ReservedServicesList } from 'Components/widgets/reserved-services-wrapper/ReservedServicesList';
import { B2CAppContext } from 'Context/ReactAppContext';
import { trackNavigation } from 'Views/booking/BookingEngine.tracking';
import { getInsurancesStore, getReservedServicesByAttendee } from 'Store/booking/booking.selector';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isOptionAllowed } from 'Utils/proposal/proposalUtils';
import { getChinaTimerSessionKey } from 'Views/booking/BookingStep';
import { setOptionValidity } from 'Store/booking/option-validity/optionvalidity.action';
import { BOOKING_ALREADY_EXISTS } from 'Utils/option-validity/constants';
import { getDiffFromNowTo } from 'Utils/date/apiDate';
import { getKeyValue, t } from 'Src/i18n';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { AccordionBlock } from 'Components/widgets/accordion-block/AccordionBlock';
import { Icon } from 'Components/frameworks/icon/Icon';
import { AccommodationSummary } from 'Components/widgets/accommodation-summary/AccommodationSummary';
import { PriceDetailLines } from 'Components/widgets/price-detail-block-lines/PriceDetailLines';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { getTotalPriceWithDonation } from 'Components/utils/price/price.util';
import { GreatMembersLoyaltyPoints } from 'Components/widgets/great-members-loyalty-points/GreatMembersLoyaltyPoints';
import {
  ADD_INSURANCE,
  DELETE_INSURANCE,
  INVALID_INSURANCE,
} from './insurance/insurance.constantes';
import { Insurance } from './insurance/Insurance';
import { insuranceFieldName } from './insurance/insurance.utils';
import { trackClick } from './SummaryView.tracking';
import OptionBlock from './option-block/OptionBlock';
import { FtcOptionModal } from './option-modal/FtcOptionModal';
import {
  getPriceDetail,
  getSummaryViewLinesByProposal,
  getOffersAndDiscounts,
} from './SummaryView.utils';
const {
  PACKAGES: { JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES },
} = constants;
type Props = {
  i18n: I18N;
  actionGA: string;
  resort: Product;
  selectedProposal: Proposal;
  proposals: Array<Proposal>;
  setTimerInitialValue: (id: number) => void;
  bookingStep: string;
  showTimer?: boolean;
  infoBannerData: InfoBannerData;
  isOptionHoldOnly: boolean;
};
const initialState = {
  isLoading: false,
  toggleOptionModal: false,
  isFtcPopinOpened: false,
  isContinueActionClicked: false,
  openBookingAlreadyExistsErrorMessage: false,
  duration: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'toggleLoading':
      return { ...state, isLoading: !state.isLoading };

    case 'updateInsuranceError':
      return {
        ...state,
        insuranceError: { ...state.insuranceError, ...action.payload },
      };

    case 'openOptionModal':
      return { ...state, toggleOptionModal: !state.toggleOptionModal };

    case 'toggleFtcPopin':
      return { ...state, isFtcPopinOpened: !state.isFtcPopinOpened };

    case 'setContinueActionClicked':
      return { ...state, isContinueActionClicked: action.payload };

    case 'setOpenBookingAlreadyExistsErrorMessage':
      return { ...state, openBookingAlreadyExistsErrorMessage: action.payload };

    case 'setDuration':
      return { ...state, duration: action.payload };

    default:
      return state;
  }
};

const SummaryView = ({
  i18n,
  infoBannerData,
  isOptionHoldOnly,
  proposals,
  resort,
  selectedProposal,
  setTimerInitialValue,
  showTimer,
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const router = useRouter();
  const context = useContext(B2CAppContext);
  const { isTablet } = useResponsive();
  const isMobile = !isTablet;
  const priceWithDonation = getTotalPriceWithDonation(selectedProposal.price, context.locale);
  const { isJoyview, isUrbanOasis } = resort;
  const isJoyViewAllInclusive = selectedProposal.packageId === JOYVIEW_ALL_INCLUSIVE && isJoyview;
  const isUrbanOasisAllInclusive =
    selectedProposal.packageId === JOYVIEW_ALL_INCLUSIVE && isUrbanOasis;
  const isUrbanOasisFoodAndBeverage =
    selectedProposal.packageId === JOYVIEW_FOOD_AND_BEVERAGES && isUrbanOasis;
  const { discounts, offers } = getOffersAndDiscounts(selectedProposal?.price?.discounts || []);
  const dispatch = useDispatch();

  const onLoadingStart = () => {
    return dispatch(startLoading());
  };

  const onLoadingEnd = () => {
    return dispatch(endLoading());
  };

  const onDeleteInsuranceFromProposals = (
    addOrRemoveInsuranceParams: AddOrRemoveInsuranceFromProposal,
  ) => {
    return dispatch(deleteInsurances(addOrRemoveInsuranceParams));
  };

  const onAddInsuranceToProposals = (
    addOrRemoveInsuranceParams: AddOrRemoveInsuranceFromProposal,
  ) => {
    return dispatch(addInsurances(addOrRemoveInsuranceParams));
  };

  const onSetOptionValidity = () => {
    return dispatch(setOptionValidity(context));
  };

  const onRefuseInsuranceAction = (insuranceId) => {
    return dispatch(refuseInsuranceAction(insuranceId));
  };

  const getInitialInsuranceState = () => {
    const insuranceError = selectedProposal.additionalServices.insurance?.reduce((acc, ins) => {
      const { id, isPartiallyRented } = ins.pricingInfo;
      return { ...acc, [id]: isPartiallyRented ? ADD_INSURANCE : '' };
    }, {});
    return insuranceError;
  };

  const [state, componentDispatch] = useReducer(reducer, {
    ...initialState,
    insuranceError: getInitialInsuranceState(),
  });
  const reservedServicesByAttendee = useSelector(getReservedServicesByAttendee);
  const insurances = useSelector(getInsurancesStore);

  const hasNotRefusedNeitherCheckedCgusOfSomeInsurances = () => {
    return values(insurances).some((ins) => !ins?.termsChecked && !ins?.isDeleted);
  };

  useEffect(() => {
    if (showTimer) {
      const newDateTime: number = moment().add(15, 'minutes').unix();
      // initialize timer only in summary step for chinese locale and proposals with transport
      setTimerInitialValue(newDateTime);
      sessionStorage.setItem(getChinaTimerSessionKey(selectedProposal.id), newDateTime.toString());
    }

    onSetOptionValidity();

    if (selectedProposal.expirationDateTime) {
      componentDispatch({
        type: 'setDuration',
        payload: getDiffFromNowTo(selectedProposal?.expirationDateTime),
      });
    }
  }, []);
  const areInsurancesChoicesValid = useCallback(() => {
    let hasError = false;
    const insurancesActualState = state.insuranceError;
    const insurancesStateToCheck = state.insuranceError;

    for (const insuranceId in insurancesActualState) {
      if (
        (isEmpty(insurancesActualState[insuranceId]) ||
          insurancesActualState[insuranceId] === INVALID_INSURANCE) &&
        !insurances[insuranceId].isDeleted
      ) {
        hasError = true;
        scrollToElement('#form-' + insuranceFieldName + kebabCase(insuranceId));
        Object.assign(insurancesStateToCheck, {
          [insuranceId]: INVALID_INSURANCE,
        });
      }
    }

    if (
      hasError ||
      (selectedProposal?.additionalServices.insurance?.length !== 0 &&
        hasNotRefusedNeitherCheckedCgusOfSomeInsurances())
    ) {
      trackNavigation('summary', 'insuranceError', context);
      componentDispatch({
        type: 'updateInsuranceError',
        payload: {
          insurancesStateToCheck,
        },
      });
      componentDispatch({
        type: 'setContinueActionClicked',
        payload: true,
      });
      return Promise.reject();
    }

    return Promise.resolve();
  }, [selectedProposal, state.insuranceError, insurances]);
  useBookingNavbar({
    isPriceLoading: state.isLoading,
    noEdit: false,
    overwriteContinueButton: isOptionHoldOnly,
    validationCallback: areInsurancesChoicesValid,
  });

  // add, remove or refuse an insurance
  const insuranceActions = (insurance: UpsellInsurance, action: string) => {
    componentDispatch({
      type: 'toggleLoading',
    });
    const insuranceId = insurance.pricingInfo.id;
    const proposalIds = proposals.map(({ id }) => id);
    const lightAttendee = selectedProposal.attendees
      ? selectedProposal.attendees.list.map(({ id }) => ({
          id,
        }))
      : [];
    const addOrRemoveInsuranceParams = {
      proposalIds,
      insurance,
      attendeeIds: lightAttendee.map(({ id }) => id),
      appContext: context,
    };

    if (action === ADD_INSURANCE) {
      trackNavigation('summary', 'insuranceSubscribe', context);

      const addInsuranceStateById = () => {
        componentDispatch({
          type: 'toggleLoading',
        });
        componentDispatch({
          type: 'updateInsuranceError',
          payload: {
            [insuranceId]: ADD_INSURANCE,
          },
        });
      };

      onAddInsuranceToProposals(addOrRemoveInsuranceParams).then(addInsuranceStateById);
    } else {
      // ACTION REMOVE
      trackNavigation('summary', 'insuranceNotSubscribe', context);

      const deleteInsuranceStateById = () => {
        componentDispatch({
          type: 'toggleLoading',
        });
        componentDispatch({
          type: 'updateInsuranceError',
          payload: {
            [insuranceId]: DELETE_INSURANCE,
          },
        });
      };

      const isInsuranceAlreadyAddedToProposal = insurance.pricingInfo.isPartiallyRented;

      if (isInsuranceAlreadyAddedToProposal) {
        onDeleteInsuranceFromProposals(addOrRemoveInsuranceParams).then(deleteInsuranceStateById);
      } else {
        // equivalent to just refuse the insurance, no request is involved in this case
        deleteInsuranceStateById();
      }

      onRefuseInsuranceAction(insuranceId);
    }
  };

  const takeAnOption = async (withfutureTravelCredits: boolean = false) => {
    trackClick('saveOption_popin', context);
    onLoadingStart();
    const status = await submitOption({
      appContext: context,
      router,
      hasSaveOption: true,
      withfutureTravelCredits,
    })(selectedProposal.id);

    if (!status?.isRedirectionPerfomed && status?.reason === BOOKING_ALREADY_EXISTS) {
      componentDispatch({
        type: 'toggleLoading',
      });
      componentDispatch({
        type: 'setOpenBookingAlreadyExistsErrorMessage',
        payload: true,
      });
      onLoadingEnd();
      setTimeout(() => scrollToElement('#bloc-option'));
    }
  };

  const loadAndTakeAnOption = async (boolean) => {
    if (!state.isLoading) {
      onLoadingStart;
      await takeAnOption(boolean);
      onLoadingEnd;
    }
  };

  const shouldShowOptionButton = (): boolean => {
    return (
      isFeatureFlipped(context, isMobile)('booking.option') && isOptionAllowed(selectedProposal)
    );
  };

  const isPromoCodeEnabled = getKeyValue('booking.priceDetails.promoCode.enabled');

  const openOptionModal = () => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_SUMMARY, {
      detail_click: 'hold_option',
    });
    componentDispatch({
      type: 'openOptionModal',
    });
  };

  const getDisclaimer = () =>
    selectedProposal?.durationDisclaimer === false ? t('booking.option.DurationDisclaimer') : '';

  const roomQuantity = selectedProposal.accommodations
    .filter((accommodation) => accommodation.quantity > 1)
    .map((accommodation) => accommodation.quantity);
  const shouldShowPriceDetails =
    isJoyViewAllInclusive || isUrbanOasisAllInclusive || (!isJoyview && !isUrbanOasis);
  return (
    <Fragment>
      <Head>
        <title key="title">{t('booking.summary.seo.title')}</title>
      </Head>
      {renderInfoBanner({
        infoBannerData,
        departureDate: selectedProposal.departureDate,
        arrivalDate: selectedProposal.arrivalDate,
        currentStep: 'summary',
      })}
      <p className="mb-10 font-brand text-24 text-black">{t('booking.summary.pageTitle')}</p>
      <div className="mb-20 flex items-center gap-10">
        <Icon className="text-green" name={ICONS.CHECK_FILLED} width="1.875rem" />
        <p className="mt-2 font-brand text-green">
          {t('booking.summary.discount.bestRateGuarantee')}
        </p>
      </div>
      <div className="mb-60 rounded-12 bg-white p-20 shadow-sm" data-testid="stay-summary-ticket">
        <AccordionBlock
          trackingEventName={TRACK_EVENT_NAMES.CLICK_SUMMARY}
          lines={getSummaryViewLinesByProposal({
            accommodationSummary: (
              <AccommodationSummary
                accommodations={selectedProposal.accommodations}
                resort={resort}
                roomQuantity={roomQuantity}
              />
            ),
            locale: context.locale,
            selectedProposal,
            isJoyViewOrUrbanOasisAllInclusive: isJoyViewAllInclusive || isUrbanOasisAllInclusive,
            isUrbanOasisFoodAndBeverage,
            i18n,
          })}
        >
          <div className="mt-40">
            {shouldShowPriceDetails && (
              <p className="mb-20 font-bold">
                {t('booking.priceDetails.packagePrice.priceDetails')}
              </p>
            )}
            <PriceDetailLines
              trackingEventName={TRACK_EVENT_NAMES.CLICK_SUMMARY}
              priceDetail={getPriceDetail({
                selectedProposal,
                priceWithDonation,
                showPriceDetailLines: shouldShowPriceDetails,
                promoCode: (
                  <div className="mb-20">
                    <DiscountsLine discounts={offers} />
                    {isPromoCodeEnabled && !!selectedProposal && (
                      <PromoCode
                        proposalId={selectedProposal.id}
                        customerId={parseInt(selectedProposal.attendees.list[0].customerId, 10)}
                        discounts={discounts}
                      />
                    )}
                  </div>
                ),
                insurance: (
                  <div className="mb-20">
                    {selectedProposal.additionalServices.insurance
                      ? selectedProposal.additionalServices.insurance.map((insurance) => (
                          <Insurance
                            isLoading={state.isLoading}
                            errorMessageCode={state.insuranceError[insurance.pricingInfo.id]}
                            key={insurance.pricingInfo.id}
                            insurance={insurance}
                            onChange={insuranceActions}
                            isContinueActionClicked={state.isContinueActionClicked}
                            dataTestId="insurance-block"
                          />
                        ))
                      : null}
                  </div>
                ),
              })}
            />
            {!!selectedProposal.greatMembersLoyaltyPoints && (
              <GreatMembersLoyaltyPoints
                loyaltyPoints={selectedProposal.greatMembersLoyaltyPoints}
                dataTestId="greatMembersLoyaltyPoints"
              />
            )}
          </div>
        </AccordionBlock>
      </div>
      {reservedServicesByAttendee.length > 0 && (
        <div className="mb-20">
          <Title className="mb-10" theme="title-brand-n2">
            {t('booking.activities.myPackage.title')}
          </Title>
          <ReservedServicesList
            reservedServicesByAttendee={reservedServicesByAttendee}
            noEditMode
            currency={selectedProposal.price.currency}
            scrollBackwardLabel={t('common.scrollBackward')}
            scrollForwardLabel={t('common.scrollForward')}
          />
        </div>
      )}

      <OptionBlock
        showOptionButton={shouldShowOptionButton()}
        onOptionTaking={() => loadAndTakeAnOption(false)}
        duration={state.duration}
        disclaimer={getDisclaimer()}
        isBookingAlreadyExistsErrorMessageOpen={state.openBookingAlreadyExistsErrorMessage}
        durationDisclaimer={selectedProposal?.durationDisclaimer}
        proposalId={selectedProposal?.id}
        isOptionAllowed={isOptionAllowed(selectedProposal)}
        resortImage={resort.image}
        onOptionButtonClick={openOptionModal}
        onOptionModalClose={() =>
          componentDispatch({
            type: 'openOptionModal',
          })
        }
        toggleOptionModal={state.toggleOptionModal}
      />
      {state.isFtcPopinOpened && (
        <FtcOptionModal
          onClose={() =>
            componentDispatch({
              type: 'toggleFtcPopin',
            })
          }
          imageUrl={resort.image}
          icon={ICONS.PROMO_CODE}
          onClick={() => loadAndTakeAnOption(true)}
          proposalId={selectedProposal?.id}
        />
      )}
    </Fragment>
  );
};

export default SummaryView;
